const API_URL = '/api';

// Get auth token
const getToken = () => localStorage.getItem('token');

// Base fetch with auth
export const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers
  });

  // Handle unauthorized
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  return res;
};

// Game APIs
export const gamesApi = {
  list: async () => {
    const res = await apiFetch('/games');
    if (!res.ok) throw new Error('Failed to fetch games');
    return res.json();
  },
  
  get: async (gameId) => {
    const res = await apiFetch(`/games/${gameId}`);
    if (!res.ok) throw new Error('Failed to fetch game');
    return res.json();
  },
  
  getConfig: async (gameId) => {
    const res = await apiFetch(`/games/${gameId}/config`);
    if (!res.ok) throw new Error('Failed to fetch game config');
    return res.json();
  }
};

// Progress APIs
export const progressApi = {
  get: async (gameId, childId = null) => {
    const url = childId 
      ? `/progress/${gameId}?childId=${childId}`
      : `/progress/${gameId}`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error('Failed to fetch progress');
    return res.json();
  },
  
  update: async (gameId, data) => {
    const res = await apiFetch(`/progress/${gameId}/update`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.message || 'Failed to update progress');
    }
    return res.json();
  },
  
  startSession: async (gameId, childId = null) => {
    const res = await apiFetch(`/progress/${gameId}/session`, {
      method: 'POST',
      body: JSON.stringify({ action: 'start', childId })
    });
    if (!res.ok) throw new Error('Failed to start session');
    return res.json();
  },
  
  endSession: async (gameId, sessionData) => {
    const res = await apiFetch(`/progress/${gameId}/session`, {
      method: 'POST',
      body: JSON.stringify({ action: 'end', ...sessionData })
    });
    if (!res.ok) throw new Error('Failed to end session');
    return res.json();
  },
  
  getAll: async () => {
    const res = await apiFetch('/progress');
    if (!res.ok) throw new Error('Failed to fetch progress');
    return res.json();
  }
};

// Children APIs
// The child profile is the record every piece of engine progress hangs off.
export const childrenApi = {
  list: async () => {
    const res = await apiFetch('/children');
    if (!res.ok) throw new Error('Failed to load children');
    return res.json();
  },

  get: async (id) => {
    const res = await apiFetch(`/children/${id}`);
    if (!res.ok) throw new Error('Failed to load child');
    return res.json();
  },

  // { name, birthDate } - birthDate is ISO yyyy-mm-dd.
  // Surfaces the server's field-level validation messages rather than a
  // generic failure, so the form can tell the parent what to fix.
  create: async ({ name, birthDate }) => {
    const res = await apiFetch('/children', {
      method: 'POST',
      body: JSON.stringify({ name, birthDate })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(fieldError(data) || 'Failed to create child');
    }
    return res.json();
  },

  update: async (id, data) => {
    const res = await apiFetch(`/children/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const data2 = await res.json().catch(() => null);
      throw new Error(fieldError(data2) || 'Failed to update child');
    }
    return res.json();
  }
};

// GAME-LEVEL-ENGINE APIs
export const engineApi = {
  // Record one speech attempt. `components` must carry a result for every box
  // the level measures - ask framework.getBoxesForLevel(phase, level), never
  // hardcode the count. A 400 here means the game measured the wrong set.
  recordAttempt: async (payload) => {
    const res = await apiFetch('/engine/attempts', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      const err = new Error(data?.error || 'Failed to record attempt');
      // The status is what tells a caller whether retrying can possibly help:
      // 503 is "the migration has not run yet", 400 is "this game measured the
      // wrong set of boxes and always will". See components/game/shared/attemptQueue.js.
      err.status = res.status;
      err.details = data?.details;
      err.requiredBoxes = data?.requiredBoxes;
      throw err;
    }
    return res.json();
  },

  getBoxes: async (childId, gameId = null) => {
    const url = gameId
      ? `/engine/children/${childId}/boxes?gameId=${encodeURIComponent(gameId)}`
      : `/engine/children/${childId}/boxes`;
    const res = await apiFetch(url);
    if (!res.ok) throw new Error('Failed to load box scores');
    return res.json();
  },

  // The full 208-box chart. verify=true additionally cross-checks the engine
  // result against the SQL views and reports any disagreement.
  getChart: async (childId, { verify = false } = {}) => {
    const res = await apiFetch(`/engine/children/${childId}/chart${verify ? '?verify=1' : ''}`);
    if (!res.ok) throw new Error('Failed to load chart');
    return res.json();
  }
};

// Pull the first useful message out of an express-validator or plain error body.
function fieldError(data) {
  if (!data) return null;
  if (Array.isArray(data.errors) && data.errors.length) return data.errors[0].msg;
  return data.error || null;
}

// Subscription APIs
export const subscriptionApi = {
  getStatus: async () => {
    const res = await apiFetch('/subscriptions/status');
    if (!res.ok) throw new Error('Failed to fetch subscription status');
    return res.json();
  },
  
  createCheckout: async (plan) => {
    const res = await apiFetch('/subscriptions/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ plan })
    });
    if (!res.ok) throw new Error('Failed to create checkout');
    return res.json();
  },
  
  createPortal: async () => {
    const res = await apiFetch('/subscriptions/create-portal', {
      method: 'POST'
    });
    if (!res.ok) throw new Error('Failed to create portal');
    return res.json();
  }
};

// Plans APIs (public)
export const plansApi = {
  getAll: async () => {
    const res = await fetch('/api/plans');
    if (!res.ok) throw new Error('Failed to fetch plans');
    return res.json();
  },
  
  get: async (id) => {
    const res = await fetch(`/api/plans/${id}`);
    if (!res.ok) throw new Error('Failed to fetch plan');
    return res.json();
  }
};

// Settings APIs
export const settingsApi = {
  // Public - no auth required (for GA injection on page load)
  getPublic: async () => {
    const res = await fetch('/api/settings/public');
    if (!res.ok) throw new Error('Failed to fetch public settings');
    return res.json();
  },
  
  // Admin only - requires auth
  getAll: async () => {
    const res = await apiFetch('/settings');
    if (!res.ok) throw new Error('Failed to fetch settings');
    return res.json();
  },
  
  get: async (key) => {
    const res = await apiFetch(`/settings/${key}`);
    if (!res.ok) throw new Error('Failed to fetch setting');
    return res.json();
  },
  
  update: async (key, value) => {
    const res = await apiFetch(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update setting');
    }
    return res.json();
  },
  
  bulkUpdate: async (settings) => {
    const res = await apiFetch('/settings', {
      method: 'PATCH',
      body: JSON.stringify({ settings })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update settings');
    }
    return res.json();
  }
};

// Lead capture APIs (public - the marketing forms, no auth)
export const leadsApi = {
  // Throws on any non-2xx so the caller can show a real error instead of a
  // success confirmation the server never agreed to.
  submit: async (payload) => {
    const res = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      // Non-JSON body (e.g. an HTML error page from the proxy).
    }

    if (!res.ok) {
      throw new Error(data?.error || 'We could not save your submission. Please try again.');
    }

    return data;
  }
};

// Admin APIs
export const adminApi = {
  getStats: async () => {
    const res = await apiFetch('/admin/stats');
    if (!res.ok) throw new Error('Failed to fetch stats');
    return res.json();
  },
  
  getUsers: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await apiFetch(`/admin/users?${query}`);
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },
  
  getUser: async (id) => {
    const res = await apiFetch(`/admin/users/${id}`);
    if (!res.ok) throw new Error('Failed to fetch user');
    return res.json();
  },
  
  createUser: async (data) => {
    const res = await apiFetch('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to create user');
    }
    return res.json();
  },
  
  updateUser: async (id, data) => {
    const res = await apiFetch(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update user');
    }
    return res.json();
  },
  
  deleteUser: async (id) => {
    const res = await apiFetch(`/admin/users/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete user');
    }
    return res.json();
  },
  
  resetUserProgress: async (id, gameId = null) => {
    const res = await apiFetch(`/admin/users/${id}/reset-progress`, {
      method: 'POST',
      body: JSON.stringify({ gameId })
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to reset progress');
    }
    return res.json();
  },
  
  // What the registered games cover, straight from the registry. The admin
  // game creator asks the server rather than importing the games' content,
  // because coverage is a fact about a build and the server holds it.
  getGames: async () => {
    const res = await apiFetch('/admin/games');
    if (!res.ok) throw new Error('Failed to fetch games');
    return res.json();
  },

  getLeads: async (status = null) => {
    const url = status ? `/admin/leads?status=${status}` : '/admin/leads';
    const res = await apiFetch(url);
    if (!res.ok) throw new Error('Failed to fetch leads');
    return res.json();
  },
  
  updateLead: async (id, data) => {
    const res = await apiFetch(`/admin/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update lead');
    return res.json();
  },
  
  exportContacts: async (filters = {}) => {
    const res = await apiFetch('/admin/export/contacts', {
      method: 'POST',
      body: JSON.stringify({ format: 'csv', filters })
    });
    if (!res.ok) throw new Error('Failed to export contacts');
    return res.blob();
  },

  // Plans management
  getPlans: async () => {
    const res = await apiFetch('/plans/all');
    if (!res.ok) throw new Error('Failed to fetch plans');
    return res.json();
  },
  
  createPlan: async (planData) => {
    const res = await apiFetch('/plans', {
      method: 'POST',
      body: JSON.stringify(planData)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to create plan');
    }
    return res.json();
  },
  
  updatePlan: async (id, data) => {
    const res = await apiFetch(`/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update plan');
    }
    return res.json();
  },
  
  deletePlan: async (id) => {
    const res = await apiFetch(`/plans/${id}`, {
      method: 'DELETE'
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete plan');
    }
    return res.json();
  }
};
